import { useEffect, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { usePermissions } from "@/context/PermissionsContext";
import { useAIContext } from "@/context/AIContextProvider";
import { TID } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import ObjectivesTab from "@/components/hseq/ObjectivesTab";
import WWCCGuardPanel from "@/components/hseq/WWCCGuardPanel";
import FmGuardPanel from "@/components/hseq/FmGuardPanel";
import { ShieldAlert, HardHat, Leaf, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import SustainabilityView from "./sustainability/SustainabilityView";
import ClientSafetyReview from "./hseq/ClientSafetyReview";
import { CURRENT_FY, FY_OPTIONS, SiteHeroTile } from "./hseq/tabs/_shared";
import { OverviewTab } from "./hseq/tabs/OverviewTab";
import { IncidentsTab } from "./hseq/tabs/IncidentsTab";
import { MyFieldSafetyView } from "./hseq/MyFieldSafetyView";
import { TRIFRTab } from "./hseq/tabs/TRIFRTab";
import { AuditsTab } from "./hseq/tabs/AuditsTab";
import { TaskAuditsTab } from "./hseq/tabs/TaskAuditsTab";
import { ActionsTab } from "./hseq/tabs/ActionsTab";
import { IncidentDetailSheet } from "./hseq/tabs/IncidentDetailSheet";

export default function HSEQPage() {
  const { can, loaded, role } = usePermissions();
  const { setContext } = useAIContext();
  const isClient = (role || "").toLowerCase() === "client";
  const isAdvisor = (role || "").toLowerCase() === "sustainability_advisor";
  const sustainabilityOnly = isClient || isAdvisor;

  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return ["overview", "incidents", "trifr", "audits", "task-audits", "actions", "objectives"].includes(t) ? t : "overview";
  });
  const [subSite, setSubSite] = useState("safety");
  useEffect(() => { if (sustainabilityOnly) setSubSite("sustainability"); }, [sustainabilityOnly]);
  const [fy, setFY] = useState(CURRENT_FY);
  const [buCode, setBUCode] = useState("");

  const [reference, setReference] = useState(null);
  const [summary, setSummary] = useState(null);
  const [trifr, setTrifr] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [audits, setAudits] = useState([]);
  const [actions, setActions] = useState([]);
  const [bus, setBus] = useState([]);
  const [loading, setLoading] = useState(false);

  const [detailIncident, setDetailIncident] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ fy });
      const listParams = new URLSearchParams();
      if (buCode) {
        params.set("business_unit_code", buCode);
        listParams.set("business_unit_code", buCode);
      }
      const [
        { data: sum }, { data: trend }, { data: incs },
        { data: auds }, { data: acts },
      ] = await Promise.all([
        api.get(`/hseq/incidents/summary?${params}`),
        api.get(`/hseq/trifr?${params}`),
        api.get(`/hseq/incidents?${listParams}&limit=200`),
        api.get(`/hseq/audits${buCode ? `?business_unit_code=${buCode}` : ""}`),
        api.get(`/hseq/actions`),
      ]);
      setSummary(sum);
      setTrifr(trend.trend || []);
      setIncidents(incs);
      setAudits(auds);
      setActions(acts);
    } catch (e) {
      console.error("[HSEQ] failed to load module data", e?.message || e);
      toast.error("Failed to load HSEQ data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loaded || !can("hseq", "view")) return;
    api.get("/hseq/reference")
      .then((r) => setReference(r.data))
      .catch((e) => console.warn("[HSEQ] failed to load reference", e?.message || e));
    api.get("/org/business-units")
      .then((r) => setBus((r.data || []).filter((b) => b.active !== false)))
      .catch((e) => console.warn("[HSEQ] failed to load business units", e?.message || e));
    /* eslint-disable-next-line */
  }, [loaded]);

  useEffect(() => {
    if (!loaded || !can("hseq", "view") || sustainabilityOnly || (role || "").toLowerCase() === "field_worker") return;
    load();
    /* eslint-disable-next-line */
  }, [fy, buCode, loaded]);

  useEffect(() => {
    if (!summary) return;
    setContext({
      module: "hseq",
      fy,
      business_unit: buCode || "All BUs",
      total_incidents: summary.total_incidents,
      trifr_200k: summary.trifr_200k,
      trifr_1m: summary.trifr_1m,
      lti_count: summary.lti_count,
      open_actions: summary.open_actions,
      classification_breakdown: summary.classification_breakdown,
      category_breakdown: summary.category_breakdown,
      top_bu_incidents: summary.bu_breakdown?.slice(0, 3),
    });
    return () => setContext(null);
    /* eslint-disable-next-line */
  }, [summary, fy, buCode]);

  const syncAudits = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/hseq/audits/sync");
      toast.success(`Synced ${data.synced_count} audits from Aroflo`);
      load();
    } catch (e) {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const canCreate = can("hseq", "create"); // eslint-disable-line no-unused-vars

  if (!loaded)
    return (
      <AppLayout title="HSEQ">
        <div className="h-64 rounded-lg bg-white border border-slate-200 shimmer" />
      </AppLayout>
    );
  if (!can("hseq", "view") && !can("sustainability", "view")) {
    return (
      <AppLayout title="HSEQ">
        <div className="max-w-md mx-auto mt-16 text-center">
          <ShieldAlert className="w-6 h-6 text-rose-700 inline-block" />
          <h2 className="font-display text-xl font-semibold text-slate-900 mt-3">Access restricted</h2>
        </div>
      </AppLayout>
    );
  }

  if ((role || "").toLowerCase() === "field_worker") {
    return (
      <AppLayout title="My Safety" subtitle="Your personal HSEQ view — JSA performance, CCEW checks and vehicle">
        <MyFieldSafetyView />
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title="HSEQ"
      subtitle="Health, Safety, Environment & Quality — integrated with SharePoint & Aroflo"
      actions={
        <div className="flex items-center gap-2">
          <Select value={fy} onValueChange={setFY}>
            <SelectTrigger data-testid={TID.hseqFYSelect} className="w-28"><SelectValue /></SelectTrigger>
            <SelectContent>{FY_OPTIONS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={buCode || "__all"} onValueChange={(v) => setBUCode(v === "__all" ? "" : v)}>
            <SelectTrigger data-testid={TID.hseqBUSelect} className="w-52"><SelectValue placeholder="All BUs" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All BUs</SelectItem>
              {bus.map((b) => <SelectItem key={b.id} value={b.code}>{b.code} · {b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      }
    >
      {!sustainabilityOnly && (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
        <SiteHeroTile
          testId="hseq-safety-tile"
          selected={subSite === "safety"}
          onClick={() => setSubSite("safety")}
          icon={HardHat}
          title="Safety Site"
          subtitle="Injuries, JSAs, SWMS, TRIFR, Audits, Corrective Actions"
          statLine={summary ? `${summary.total_incidents} incidents · TRIFR ${summary.trifr_1m}` : "…"}
          accent="from-gbe to-slate-800"
        />
        <SiteHeroTile
          testId="hseq-sustainability-tile"
          selected={subSite === "sustainability"}
          onClick={() => setSubSite("sustainability")}
          icon={Leaf}
          title="Sustainability Site"
          subtitle="Emissions · Waste · Water · Energy · Habitat & Biodiversity · People"
          accent="from-emerald-800 to-emerald-950"
        />
      </div>
      )}

      {isClient ? (
        <Tabs defaultValue="sustainability">
          <TabsList className="bg-slate-50 border border-slate-200 p-1 h-auto mb-4">
            <TabsTrigger data-testid="hseq-client-tab-sustainability" value="sustainability" className="text-xs data-[state=active]:bg-white">Sustainability</TabsTrigger>
            <TabsTrigger data-testid="hseq-client-tab-safety-review" value="safety-review" className="text-xs data-[state=active]:bg-white">Company Safety Review</TabsTrigger>
          </TabsList>
          <TabsContent value="sustainability">
            <SustainabilityView fy={fy} buCode={buCode} bus={bus} />
          </TabsContent>
          <TabsContent value="safety-review">
            <ClientSafetyReview fy={fy} />
          </TabsContent>
        </Tabs>
      ) : (subSite === "sustainability" || sustainabilityOnly) ? (
        <SustainabilityView fy={fy} buCode={buCode} bus={bus} />
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-50 border border-slate-200 p-1 h-auto">
            <TabsTrigger data-testid={TID.hseqTab("overview")}  value="overview"  className="text-xs data-[state=active]:bg-white">Overview</TabsTrigger>
            <TabsTrigger data-testid={TID.hseqTab("incidents")} value="incidents" className="text-xs data-[state=active]:bg-white">Incident Register</TabsTrigger>
            <TabsTrigger data-testid={TID.hseqTab("trifr")}     value="trifr"     className="text-xs data-[state=active]:bg-white">TRIFR</TabsTrigger>
            <TabsTrigger data-testid={TID.hseqTab("audits")}    value="audits"    className="text-xs data-[state=active]:bg-white">Safety Audits <span className="ml-1 text-[9px] text-amber-700">Aroflo</span></TabsTrigger>
            <TabsTrigger data-testid="hseq-tab-task-audits"     value="task-audits" className="text-xs data-[state=active]:bg-white">Task Audits <span className="ml-1 text-[9px] text-emerald-700">JSA/CCEW</span></TabsTrigger>
            <TabsTrigger data-testid={TID.hseqTab("actions")}   value="actions"   className="text-xs data-[state=active]:bg-white">Corrective Actions</TabsTrigger>
            <TabsTrigger data-testid="hseq-tab-fm-guard"        value="fm-guard"  className="text-xs data-[state=active]:bg-white">Client Compliance</TabsTrigger>
            <TabsTrigger data-testid="hseq-tab-objectives"      value="objectives" className="text-xs data-[state=active]:bg-white">Quality & Planning Objectives</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            <OverviewTab summary={summary} trifr={trifr} loading={loading} />
          </TabsContent>
          <TabsContent value="incidents" className="mt-4">
            <IncidentsTab incidents={incidents} loading={loading} onSelect={setDetailIncident} />
          </TabsContent>
          <TabsContent value="trifr" className="mt-4">
            <TRIFRTab trifr={trifr} summary={summary} />
          </TabsContent>
          <TabsContent value="audits" className="mt-4">
            <AuditsTab audits={audits} onSync={syncAudits} syncing={syncing} canSync={can("hseq", "edit")} />
          </TabsContent>
          <TabsContent value="task-audits" className="mt-4">
            <TaskAuditsTab canManage={["admin", "executive", "finance_admin", "bu_manager"].includes((role || "").toLowerCase())}
                           isExecutive={(role || "").toLowerCase() === "executive"} />
          </TabsContent>
          <TabsContent value="actions" className="mt-4">
            <ActionsTab actions={actions} incidents={incidents} />
          </TabsContent>
          <TabsContent value="fm-guard" className="mt-4 space-y-6">
            <FmGuardPanel />
            <div className="border-t border-slate-200 pt-5" data-testid="wwcc-guard-section">
              <WWCCGuardPanel />
            </div>
          </TabsContent>
          <TabsContent value="objectives" className="mt-4">
            <ObjectivesTab />
          </TabsContent>
        </Tabs>
      )}


      <IncidentDetailSheet incident={detailIncident} onClose={() => setDetailIncident(null)} />
    </AppLayout>
  );
}
